/* eslint-disable no-nested-ternary */
import { Chat, NewReleases } from '@mui/icons-material';
import { Box, IconButton, Rating, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Review } from '../../apiTypes';
import { Column } from '../../components/DataTable';
import DataTablePaginated from '../../components/DataTablePaginated';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { usePagination } from '../../utils/api';
import { usePrevious } from '../../utils/usePrev';

import ReviewModal from './ReviewModal';

function findAverage(a: number | null, b: number | null, c: number | null) {
  const arr = [a, b, c].filter((i) => i !== null) as number[];

  if (arr.length === 0) {
    return 0;
  }

  const sum = arr.reduce((acc, val) => acc + val, 0);
  const average = sum / arr.length;

  return average;
}

type Row = DbDocument<Review>;

const Reviews = () => {
  const { lang } = useLocale();
  const { cafeSlug } = useAdmin();
  const { newReviews, isLoading } = useCafe();
  const initializedRef = useRef(false);
  const prevNewReviewsLength = usePrevious(newReviews.length);
  const [review, setReview] = useState<DbDocument<Review> | null>(null);

  const {
    page,
    onPageChange,
    data: reviews,
    isLoading: isReviewsLoading,
    revalidate,
    count,
  } = usePagination<Review>({
    path: `cafes/${cafeSlug}/reviews`,
    orderByField: 'date',
  });

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'status',
        title: '',
        align: 'left',
        render: (r: Row) => {
          if (r.isNew) {
            return <NewReleases color="warning" />;
          }
          return null;
        },
      },
      {
        name: 'date',
        title: t.date[lang],
        align: 'left',
        render: (r: Row) => <Typography>{format(r.date, 'dd.MM HH:mm')}</Typography>,
      },
      {
        name: 'name',
        title: t.userName[lang],
        align: 'left',
        render: (r: Row) => <Typography>{r.name}</Typography>,
      },
      {
        name: 'rate',
        title: '',
        align: 'left',
        render: (r: Row) => (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Rating readOnly value={findAverage(r.dishes, r.service, r.service)} size="small" precision={0.2} />
          </Box>
        ),
      },
      {
        name: 'comment',
        title: t.comment[lang],
        align: 'left',
        hideOnMobile: true,
        render: (r: Row) => (
          <Typography noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
            {r.comment}
          </Typography>
        ),
      },
      {
        name: 'edit',
        title: '',
        align: 'right',
        hideOnMobile: true,
        render: (r: Row) => (
          <IconButton onClick={() => setReview(r)}>
            <Chat />
          </IconButton>
        ),
      },
    ],
    [lang]
  );

  useEffect(() => {
    if (!isLoading && !isReviewsLoading) {
      initializedRef.current = true;
    }
  }, [isLoading, isReviewsLoading]);

  useEffect(() => {
    if (initializedRef.current && prevNewReviewsLength !== newReviews.length) {
      revalidate();
    }
  }, [newReviews.length, prevNewReviewsLength, revalidate]);

  return (
    <Box sx={{ maxWidth: 800, m: '0 auto', pb: 2 }}>
      <DataTablePaginated
        columns={columns}
        rows={reviews || []}
        page={page}
        count={count}
        onPageChange={onPageChange}
        onRowClick={(r) => setReview(r)}
      />

      {review && <ReviewModal review={review} onChange={revalidate} onClose={() => setReview(null)} />}
    </Box>
  );
};

export default Reviews;
